import React from "react";
import { CheckCircle, Calendar, Clock, Package, Phone } from "react-feather";
import EmptyState from "../common/EmptyState";

// Shared pieces of the Steadfast payments list and the single payment page
// (styles: .kd-sf-* in steadfast-kido.css). Logic stays in the pages; these
// only render what they are given.

export const money = (v) => (v === undefined || v === null || v === "" ? "—" : `৳ ${v}`);

// Steadfast's own labels use underscores ("partial_delivered").
export const sfLabel = (s) => (s ? String(s).replace(/_/g, " ") : "—");

export const consignmentTone = (status) => {
  if (status === "delivered") return "mint";
  if (status === "partial_delivered") return "butter";
  if (status === "cancelled") return "rose";
  return "sky";
};

export const paymentTone = (label) => (label === "paid" ? "mint" : "butter");

const orderStatusTone = (s) => (s === "Delivered" ? "mint" : s === "Cancelled" ? "rose" : "butter");

export const Badge = ({ tone, children, dot = true }) => (
  <span className={`kd-status-badge kd-st--${tone}`}>
    {dot && <span className="kd-status-dot" />}
    {children}
  </span>
);

// The payment as a remittance slip: what Steadfast paid out and what it kept.
export const PaymentSummary = ({ payment }) => (
  <div className="kd-sf-slip">
    <div className="kd-sf-slip-main">
      <span className="kd-sf-slip-label">Paid to us</span>
      <span className="kd-price-sticker kd-sf-slip-amount">{money(payment.amount)}</span>
      <div className="kd-sf-slip-tags">
        <Badge tone={paymentTone(payment.status_label)}>{sfLabel(payment.status_label)}</Badge>
        {payment.method && <span className="kd-sf-chip">{payment.method}</span>}
      </div>
    </div>
    <dl className="kd-sf-slip-facts">
      <div>
        <dt>Total</dt>
        <dd>{money(payment.total)}</dd>
      </div>
      <div className="is-bad">
        <dt>Charges</dt>
        <dd>{money(payment.charges)}</dd>
      </div>
      <div>
        <dt>Paid bills</dt>
        <dd>{payment.paid_bills ?? "—"}</dd>
      </div>
      <div className={payment.due_bills > 0 ? "is-bad" : ""}>
        <dt>Due bills</dt>
        <dd>{payment.due_bills ?? "—"}</dd>
      </div>
    </dl>
    <div className="kd-sf-slip-dates">
      <span>
        <Calendar size={12} aria-hidden="true" />
        Created {payment.created_at || "—"}
      </span>
      <span>
        <Clock size={12} aria-hidden="true" />
        Paid {payment.paid_at || "—"}
      </span>
    </div>
  </div>
);

const previewStatus = (item) => {
  if (item.notFound) return <span className="kd-muted">—</span>;
  if (item.isCancelled) return <Badge tone="rose">Cancelled</Badge>;
  if (item.alreadyApplied) return <Badge tone="butter">Already applied</Badge>;
  if (item.alreadyFullyPaid) return <Badge tone="mint">Already Paid</Badge>;
  return <Badge tone={item.paymentStatus === "Paid" ? "mint" : "sky"}>{item.paymentStatus}</Badge>;
};

const previewNote = (item) =>
  item.notFound ? "Order not found"
    : item.alreadyApplied ? "Skipped (consignment already recorded)"
    : item.isCancelled ? "Cancelled — no COD received, order status only"
    : item.alreadyFullyPaid ? "Skipped (order already fully paid)"
    : "Will apply SteadFast (COD)";

// Receive Payment: button → preview of what will be written → confirm.
// `detailed` adds the COD / already-paid columns and the cash-in total
// (single payment page).
export const ReceivePanel = ({
  payment,
  processed,
  preview,
  loading,
  confirming,
  onReceive,
  onConfirm,
  onCancel,
  detailed = false,
}) => {
  if (processed) {
    return (
      <div className="kd-sf-received">
        <CheckCircle size={16} aria-hidden="true" />
        Payment received
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="kd-sf-receive">
        <button type="button" className="btn btn-primary" onClick={onReceive} disabled={loading}>
          {loading ? "Looking up orders…" : "Receive Payment"}
        </button>
        <span className="kd-muted">
          Checks each order first. Nothing is saved until you confirm.
        </span>
      </div>
    );
  }

  const heads = detailed
    ? ["Invoice", "COD Amount", "Already Paid", "Due", "Will Apply", "Payment Status", "Order Status", "Note"]
    : ["Invoice (Order ID)", "Will Apply", "Order Total Due", "Status", "Order Status", "Note"];

  const cashOut = Number(payment.charges || 0) + Number(payment.due_bills || 0);

  return (
    <div className="kd-sf-preview">
      <h3>Confirm payment application</h3>
      <div className="kd-sf-table-wrap">
        <table className="kd-sf-table">
          <thead>
            <tr>
              {heads.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.map((item, ri) => {
              const skip = item.notFound || item.alreadyApplied || item.alreadyFullyPaid || item.isCancelled;
              const willApply = (
                <td className={skip ? "kd-muted" : "kd-sf-good"}>
                  {item.notFound ? "—" : skip ? "—" : `৳ ${item.appliedAmount}`}
                  {!item.notFound && !skip && item.appliedAmount < Number(item.consignment.cod_amount) && (
                    <span className="kd-sf-capped">capped</span>
                  )}
                </td>
              );
              return (
                <tr key={ri} className={skip ? "is-skipped" : ""}>
                  <td className="kd-sf-id">{item.consignment.invoice ?? "—"}</td>
                  {detailed ? (
                    <>
                      <td className="kd-muted">৳ {item.consignment.cod_amount ?? "—"}</td>
                      <td>{item.notFound ? "—" : `৳ ${item.alreadyPaid}`}</td>
                      <td className="kd-sf-strong">{item.notFound ? "—" : `৳ ${item.totalDue}`}</td>
                      {willApply}
                    </>
                  ) : (
                    <>
                      {willApply}
                      <td>{item.notFound ? "—" : `৳ ${item.totalDue}`}</td>
                    </>
                  )}
                  <td>{previewStatus(item)}</td>
                  <td>
                    {item.sfOrderStatus ? (
                      <Badge tone={orderStatusTone(item.sfOrderStatus)} dot={false}>
                        → {item.sfOrderStatus}
                      </Badge>
                    ) : (
                      <span className="kd-muted">—</span>
                    )}
                  </td>
                  <td className="kd-sf-note">{previewNote(item)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="kd-sf-ledger">
        {detailed && (
          <span className="kd-sf-ledger-in">
            Cash-in total: ৳
            {preview
              .filter((i) => !i.notFound && !i.alreadyApplied && !i.alreadyFullyPaid)
              .reduce((s, i) => s + (i.appliedAmount || 0), 0)}{" "}
            ({preview.filter((i) => !i.notFound && !i.alreadyApplied && !i.alreadyFullyPaid && i.appliedAmount > 0).length}{" "}
            consignment(s) to apply)
          </span>
        )}
        <span className="kd-sf-ledger-out">
          Cash-out entry: ৳{cashOut} (Charges ৳{payment.charges} + Due Bills {payment.due_bills})
        </span>
      </div>

      <div className="kd-sf-preview-actions">
        <button type="button" className="btn btn-light" onClick={onCancel} disabled={confirming}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={confirming}>
          {confirming ? "Applying…" : "Confirm & Apply"}
        </button>
      </div>
    </div>
  );
};

export const ConsignmentList = ({ consignments }) => {
  if (!consignments || consignments.length === 0) {
    return <EmptyState>No consignments found.</EmptyState>;
  }
  return (
    <div className="kd-sf-consignments">
      <h3>
        <Package size={15} aria-hidden="true" />
        Consignments
        <span className="kd-sf-count">{consignments.length}</span>
      </h3>
      <div className="kd-sf-table-wrap">
        <table className="kd-sf-table">
          <thead>
            <tr>
              {["Consignment ID", "Invoice", "Tracking", "Recipient", "COD Amount", "Status"].map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {consignments.map((c, ci) => (
              <tr key={ci}>
                <td className="kd-sf-strong">{c.consignment_id ?? "—"}</td>
                <td className="kd-sf-id">{c.invoice ?? "—"}</td>
                <td>
                  <span className="kd-sf-track">{c.tracking_code ?? "—"}</span>
                </td>
                <td>
                  <span className="kd-sf-strong">{c.recipient_name ?? "—"}</span>
                  {c.recipient_phone && (
                    <span className="kd-sf-phone">
                      <Phone size={11} aria-hidden="true" />
                      {c.recipient_phone}
                    </span>
                  )}
                </td>
                <td className="kd-sf-good">৳ {c.cod_amount ?? "—"}</td>
                <td>
                  <Badge tone={consignmentTone(c.status)}>{sfLabel(c.status)}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
